import React from 'react';
import toast from 'react-hot-toast';
import { AppointmentStatus, AwardStatus, Client, YesNoMaybeAlready } from '../api/client';
import { environment } from '../api/build';
import {  DefaultProfileNonReportedText } from '../pages/Staff/StudentDetails/StudentProfileHelpers';

export const formatCampus = (campus: string): string => {
  switch (campus) {

    case "1":
      return "Tampa";
    case "2":
      return "St. Petersburg";
    case "3":
      return "Sarasota-Manatee";
    default:
      return "Unknown Campus";
  }
};

export const stringifyAppointmentStatus = (status: AppointmentStatus): string => {
  switch (status) {
    case AppointmentStatus.Waiting:
      return "Waiting to be processed by ";
    case AppointmentStatus.Processed:
      return "Processed by ";
    case AppointmentStatus.Canceled:
      return "Appointment canceled by ";
    default:
      return "Could not find status"
  }
}

export const stringifyAwardStatus = (status: AwardStatus): string => {
  switch (status) {
    case AwardStatus.Accepted:
      return "Accepted"
    case AwardStatus.Declined:
      return "Declined"
    case AwardStatus.HonorableMention:
      return "Honorable Mention"
    default:
      return "N/A"
  }
}

// Function to remove "@usf.edu" from input to return the NetID
const suffix: string = "@usf.edu";
export function normalizeNetId(input: string): string {
  return (input.toLowerCase().endsWith(suffix) ? input.slice(0, -(suffix.length)) : input);
}

// Format netID to USF email
export function formatToUsfEmail(netID: string) {
  if (netID) return netID + "@usf.edu";
  else return "Unknown";
}

export const printGradSchoolPlans = (plans?: YesNoMaybeAlready): string => {
  switch (plans) {
    case YesNoMaybeAlready.Yes:
      return "Yes";
    case YesNoMaybeAlready.No:
      return "No";
    case YesNoMaybeAlready.Maybe:
      return "Maybe";
    case YesNoMaybeAlready.NotProvided:
      return DefaultProfileNonReportedText
    default:
      return DefaultProfileNonReportedText;
  }
}

export const formatBoolean = (bool?: Boolean): string => {
  if (bool) return "Yes";
  return "No";
}


export const isLinkedInUrl = (value: string): boolean => {
  return value.toLowerCase().includes("linkedin.com");
};

export const extractUsernameFromLinkedInUrl = (input: string): string => {
  const urlRegex = /linkedin\.com\/(?:in|profile)\/([^\/]+)(?:\/|$)/i;
  const urlMatches = input.match(urlRegex);
  if (urlMatches && urlMatches.length > 1) {
    return urlMatches[1];
  }

  const cleanedInput = input.trim().replace(/^@/, '');

  return cleanedInput;
};

interface LinkedInProfileProps {
  linkedinProfile: string | null;
}

const LinkedInProfile: React.FC<LinkedInProfileProps> = ({ linkedinProfile }) => {
  if (!linkedinProfile) {
    return (
      <div>
        <div className="font-bold">LinkedIn Profile:</div>
        <div>-</div>
      </div>
    );
  }

  const profileUrl = isLinkedInUrl(linkedinProfile)
    ? linkedinProfile
    : `https://www.linkedin.com/in/${extractUsernameFromLinkedInUrl(linkedinProfile)}`;

  return (
    <div>
      <div className="font-bold">LinkedIn Profile:</div>
      <div>
        <a
          href={profileUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-500 underline"
        >
          {linkedinProfile}
        </a>
      </div>
    </div>
  );
};

export default LinkedInProfile;


export const downloadResume = async (resumeId: string | undefined, client: Client | null) => {
  if (!resumeId) {
    toast.error("Resume ID not available");
    return;
  }

  if (!client) {
    toast.error("Error loading backend.");
    return;
  }

  toast.promise(
    (async () => {
      client?.student_GetResume(resumeId).then((response) => {
        if (!response || !response.data) {
          toast.error("404. Resume not found");
          return;
        }

        // Extract necessary information
        const blob = new Blob([response.data], { type: response.headers?.['content-type'] || 'application/octet-stream' });
        const downloadUrl = window.URL.createObjectURL(blob);

        // Create a link element and trigger the download
        const link = document.createElement("a");
        link.href = downloadUrl;

        // Extract the filename from the headers or use a default
        const contentDisposition = response.headers?.['content-disposition'];
        const match = contentDisposition?.match(/filename="([^"]+)"/);
        link.download = match ? match[1] : "resume.pdf";

        document.body.appendChild(link);
        link.click();

        // Cleanup
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);
      }).catch(() => {
        toast.error('Error to create you a student profile');
      });
    })(),
    {
      loading: 'Downloading...',
      error(e) {
        return `Failed to load resume: ${e}`;
      },
      success: 'Successfully Downloaded your resume!'
    }
  );
};

export const GetLocalFrontendBaseUrl = (): string => {
  switch (environment) {
    case "local+api":
      return "https://localhost:1236/#";

    // Production environment
    case "production":
      return "https://cloud.usf.edu/ons/#";

    // Default to local or development environment
    default:
    case "local":
    case "development":
      return "https://clouddev.usf.edu/ons/#";
  }
};